import {
  AloftDate,
  AloftDateJSON,
  AloftPrice,
  AloftPriceJSON,
  CustomMonthlyPriceTrend,
  IsoMonth,
  MarketAdjustments,
  MarketTrendsType,
  Transaction,
  applyRoundingAndThresholds,
  expected,
  isUndefined,
  schAloftDate,
  schAloftPrice,
  schAloftTime,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { makeCompMonthMap } from '../../../../modules/marketConditions/utils/makeCompMonthMap.ts';
import { createCompTransactionsFromTransactions } from '../../../../modules/marketConditions/zillow/createCompTransactionsFromTransactions.ts';
import { getHousingTrendsData } from '../../../../modules/marketConditions/zillow/getHousingTrendsData.ts';
import {
  MonthGrouping,
  ZillowHomeValueIndexTrendsData,
} from '../../../../modules/marketConditions/zillow/types.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectEffectiveDate,
  selectMarketAdjustments,
} from '../orderSelectors.ts';
import {
  selectContractDate,
  selectSaleDate,
  selectTransactionById,
  selectTransactionEntities,
} from '../propertyDataSelectors/transactionSelectors.ts';
import { selectSubjectValue } from '../selectSubjectValue.ts';
import { getShouldApplyDateOfSale } from '../utils/getShouldApplyDateOfSale.ts';
import { getShouldApplyListToSale } from '../utils/getShouldApplyListToSale.ts';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DateOfSaleMethodInput {
  export type Zillow = ZillowHomeValueIndexTrendsData & {
    zip: string;
    updatedAt: AloftDate;
  };
  export type CustomMonthlyPrice = {
    historical: MonthGrouping[];
    effectiveDate: AloftDate;
  };
}

// Creates input data for the Zillow Date of Sale method
export const makeZillowMethodInput = ({
  effectiveDate,
  compSequence,
  transactionEntities,
  zillowMethodState,
}: {
  effectiveDate: AloftDate;
  compSequence: string[];
  transactionEntities: Record<string, Transaction>;
  zillowMethodState: NonNullable<
    MarketAdjustments['cachedForms']['dateOfSale']['methodState']['zillow']
  >;
}): DateOfSaleMethodInput.Zillow | undefined => {
  const { data } = expected(zillowMethodState.trends[0]);
  const dataWithJSONConverted: MarketTrendsType = {
    ...data,
    monthly_trends: {
      ...data.monthly_trends,
      historical_data: data.monthly_trends.historical_data.map((x) => ({
        ...x,
        typicalHomeValue: x.typicalHomeValue && schAloftPrice.parse(x.typicalHomeValue),
        medianListPrice: x.medianListPrice && schAloftPrice.parse(x.medianListPrice),
      })),
      one_month_projection:
        data.monthly_trends.one_month_projection &&
        schAloftPrice.parse(data.monthly_trends.one_month_projection),
    },
  };

  const trendsData: ZillowHomeValueIndexTrendsData | undefined = getHousingTrendsData(
    true,
    effectiveDate,
    dataWithJSONConverted,
    createCompTransactionsFromTransactions(transactionEntities, compSequence),
    compSequence,
  );

  if (!trendsData) return undefined;

  const updatedAt = zillowMethodState.trends[0]?.timestamp
    ? schAloftTime.parse(zillowMethodState.trends[0]?.timestamp).toAloftDate()
    : AloftDate.fromIsoString(data.updated_date);

  return {
    ...trendsData,
    zip: data.zip,
    updatedAt,
  };
};

const getValueAtEffectiveDate = (
  effectiveDate: AloftDate,
  trends: CustomMonthlyPriceTrend[],
): AloftPriceJSON | undefined => {
  const effectiveDateAsISOMonth = syntaxFNMA.aloftDateToIsoMonth(effectiveDate);
  const effectiveDateMonthIndex = trends.findIndex((x) => x.month === effectiveDateAsISOMonth);
  if (effectiveDateMonthIndex === -1) return undefined;
  return trends.at(effectiveDateMonthIndex)?.value;
};

const deriveTrendsAsPercentages = (
  trends: CustomMonthlyPriceTrend[],
  effectiveDate: AloftDate,
  typicalValueAtEffectiveDate?: AloftPriceJSON,
): CustomMonthlyPriceTrend[] => {
  const derivedTrends: CustomMonthlyPriceTrend[] = [];
  const effectiveDateAsISOMonth = syntaxFNMA.aloftDateToIsoMonth(effectiveDate);

  // Work backward to depreciate based on the next month's value
  let nextMonthValue = schAloftPrice.parse(typicalValueAtEffectiveDate);
  trends
    .slice()
    .reverse()
    .forEach((trend) => {
      const isEffectiveDate = trend.month === effectiveDateAsISOMonth;

      const monthlyPercentTrend = schAloftPrice.parse(trend.value).toDollarLike();
      const currentValue = isEffectiveDate
        ? nextMonthValue
        : nextMonthValue.div(1 + monthlyPercentTrend / 100);

      nextMonthValue = currentValue;

      derivedTrends.unshift({
        value: currentValue.toJSON(),
        month: trend.month,
      });
    });

  return derivedTrends;
};

const getPercentChange = (value: AloftPrice, typicalValueAtEffectiveDate?: AloftPriceJSON) => {
  if (!typicalValueAtEffectiveDate || typicalValueAtEffectiveDate.unitAmount === 0) return 0;
  if (value.unitAmount === 0) return 0;

  const typicalValue = schAloftPrice.parse(typicalValueAtEffectiveDate);
  return typicalValue.sub(value).ratio(value) * 100;
};

const getMonthGrouping = (
  trend: CustomMonthlyPriceTrend,
  typicalValueAtEffectiveDate?: AloftPriceJSON,
  compMonthMap?: Record<string, string[]>,
): MonthGrouping => {
  const { month } = trend;
  const value = schAloftPrice.parse(trend.value);

  const percentChange = getPercentChange(value, typicalValueAtEffectiveDate);
  const affectedComps = compMonthMap?.[month]?.map((comp) => `C${comp}`).join(', ');

  return {
    month,
    typicalValue: value,
    percentChange,
    ...(affectedComps ? { affectedComps } : {}),
  };
};

export const makeCustomMonthlyPriceInput = ({
  effectiveDate,
  trendsAsPercentages,
  trends,
  compSequence,
  transactionEntities,
  subjectMarketPrice,
}: {
  effectiveDate: AloftDate;
  trendsAsPercentages?: boolean;
  trends: CustomMonthlyPriceTrend[];
  compSequence: string[];
  transactionEntities: Record<string, Transaction>;
  subjectMarketPrice?: AloftPriceJSON;
}): DateOfSaleMethodInput.CustomMonthlyPrice => {
  const subjectPrice = subjectMarketPrice ?? transactionEntities.subject?.originalListPrice;

  const typicalValueAtEffectiveDate =
    trendsAsPercentages && subjectPrice
      ? subjectPrice
      : getValueAtEffectiveDate(effectiveDate, trends);

  const compMonthMap = makeCompMonthMap(
    createCompTransactionsFromTransactions(transactionEntities, compSequence),
    compSequence,
  );

  const derivedTrends = trendsAsPercentages
    ? deriveTrendsAsPercentages(trends, effectiveDate, typicalValueAtEffectiveDate)
    : trends;

  return {
    historical: derivedTrends.map((derivedTrend) =>
      getMonthGrouping(derivedTrend, typicalValueAtEffectiveDate, compMonthMap),
    ),
    effectiveDate,
  };
};

export const selectCustomMonthlyPriceMethodInput = createSelector(
  [
    selectEffectiveDate,
    selectCompSequence,
    selectTransactionEntities,
    selectMarketAdjustments,
    selectSubjectValue,
  ],
  (effectiveDate, compSequence, transactionEntities, marketAdjustments, subjectValue) => {
    if (!effectiveDate) return undefined;

    const { trendsAsPercentages, trends } =
      marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice;

    return makeCustomMonthlyPriceInput({
      effectiveDate: schAloftDate.parse(effectiveDate),
      trendsAsPercentages,
      trends,
      compSequence,
      transactionEntities,
      subjectMarketPrice: subjectValue?.toJSON(),
    });
  },
);

export const selectZillowMethodInput = createSelector(
  [selectEffectiveDate, selectCompSequence, selectTransactionEntities, selectMarketAdjustments],
  (effectiveDate, compSequence, transactionEntities, marketAdjustments) => {
    const zillowMethodState = marketAdjustments.cachedForms.dateOfSale.methodState.zillow;

    if (!effectiveDate || !zillowMethodState) return undefined;
    return makeZillowMethodInput({
      effectiveDate: schAloftDate.parse(effectiveDate),
      compSequence,
      transactionEntities,
      zillowMethodState,
    });
  },
);

const getFutureAdjustedPrice = (price: AloftPrice, percentChange: number): AloftPrice =>
  price.mul(1 + percentChange / 100);

const calculateDateOfSaleAdjustment = ({
  price,
  dateOfSale,
  contractDate,
  saleDate,
  effectiveDate,
  zillowMethodInput,
  customMonthlyPriceMethodInput,
}: {
  price: AloftPrice;
  dateOfSale: MarketAdjustments['cachedForms']['dateOfSale'];
  contractDate?: IsoMonth;
  saleDate?: IsoMonth;
  effectiveDate?: AloftDate;
  zillowMethodInput?: DateOfSaleMethodInput.Zillow;
  customMonthlyPriceMethodInput?: DateOfSaleMethodInput.CustomMonthlyPrice;
}): AloftPrice => {
  const { selectedMethod, methodState } = dateOfSale;

  const adjustmentDate = contractDate ?? saleDate;
  if (isUndefined(adjustmentDate) || isUndefined(effectiveDate)) return AloftPrice.zero;

  if (selectedMethod === 'none') return AloftPrice.zero;

  if (selectedMethod === 'zillow') {
    if (isUndefined(zillowMethodInput)) return AloftPrice.zero;

    const { projection, historical } = zillowMethodInput;
    const projectionGrouping = projection?.find(({ month }) => month === adjustmentDate);
    const historicalGrouping = historical.find(({ month }) => month === adjustmentDate);

    const monthGrouping = projectionGrouping ?? historicalGrouping;

    if (isUndefined(monthGrouping)) return AloftPrice.zero;

    return price.mul(monthGrouping.percentChange / 100).roundToDollarLike();
  }

  if (selectedMethod === 'customMonthlyRate') {
    const rate = methodState.customMonthlyRate?.rate;
    if (isUndefined(rate)) return AloftPrice.zero;

    const adjustmentDateAsAloftDate = syntaxFNMA.isoMonthToAloftDate(adjustmentDate);
    const diffInMonths = effectiveDate.differenceInCalendarMonths(adjustmentDateAsAloftDate);

    const adjustedPrice = Array.from({ length: Math.abs(diffInMonths) }).reduce<AloftPrice>(
      (futureAdjustedPrice) => getFutureAdjustedPrice(futureAdjustedPrice, rate),
      price,
    );

    return adjustedPrice.sub(price).roundToDollarLike();
  }

  if (selectedMethod === 'customMonthlyPrice') {
    if (isUndefined(customMonthlyPriceMethodInput)) return AloftPrice.zero;
    const { historical } = customMonthlyPriceMethodInput;

    const applicableMonthGrouping = historical.find(({ month }) => month === adjustmentDate);
    if (isUndefined(applicableMonthGrouping)) return AloftPrice.zero;

    const adjustedPrice = getFutureAdjustedPrice(
      price,
      applicableMonthGrouping.percentChange,
    ).roundToDollarLike();

    return adjustedPrice.sub(price).roundToDollarLike();
  }

  console.warn(`Date of Sale method '${selectedMethod as string}' not implemented yet.`);
  return AloftPrice.zero;
};

const calculateListToSaleAdjustment = ({
  price,
  listToSale,
}: {
  price: AloftPrice;
  listToSale: MarketAdjustments['cachedForms']['listToSale'];
}): AloftPrice => {
  const { selectedMethod, methodState } = listToSale;

  if (selectedMethod === 'none') return AloftPrice.zero;

  if (selectedMethod === 'customRatio') {
    const ratio = methodState.customRatio?.ratio;
    if (isUndefined(ratio) || !Number.isFinite(ratio)) {
      console.warn(`List to Sale: Custom Ratio requires a numeric ratio, but received '${ratio}'.`);
      return AloftPrice.zero;
    }
    return price.mul(ratio / 100 - 1).roundToDollarLike();
  }

  console.warn(`List to Sale method '${selectedMethod as string}' not implemented yet.`);
  return AloftPrice.zero;
};

export const selectMonthlyPriceInputFromCustomRate = createSelector(
  [
    selectEffectiveDate,
    selectCompSequence,
    selectTransactionEntities,
    selectMarketAdjustments,
    selectSubjectValue,
    selectCustomMonthlyPriceMethodInput,
  ],
  (
    effectiveDateJSON,
    compSequence,
    transactionEntities,
    marketAdjustments,
    subjectValue,
    customMonthlyPriceMethodInput,
  ): DateOfSaleMethodInput.CustomMonthlyPrice | undefined => {
    const originalSubjectListPrice = transactionEntities.subject?.originalListPrice;

    const subjectPrice =
      subjectValue ?? (originalSubjectListPrice && schAloftPrice.parse(originalSubjectListPrice));
    const { rate } = marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyRate;

    if (!rate || !effectiveDateJSON || !subjectPrice) return undefined;

    const effectiveDate = schAloftDate.parse(effectiveDateJSON);
    const effectiveDateAsISOMonth = syntaxFNMA.aloftDateToIsoMonth(effectiveDate);

    const months: IsoMonth[] = customMonthlyPriceMethodInput?.historical.map((x) => x.month) ?? [];

    const trends: CustomMonthlyPriceTrend[] = months
      .slice()
      .reverse()
      .map((month) => {
        const monthAsAloftDate = syntaxFNMA.isoMonthToAloftDate(month);

        const isEffectiveDate = month === effectiveDateAsISOMonth;
        if (isEffectiveDate) return { value: subjectPrice.toJSON(), month };

        const diffInMonths = effectiveDate.differenceInCalendarMonths(monthAsAloftDate);

        const adjustedPrice = Array.from({ length: Math.abs(diffInMonths) }).reduce<AloftPrice>(
          (futureAdjustedPrice) => getFutureAdjustedPrice(futureAdjustedPrice, rate),
          subjectPrice,
        );

        return { value: adjustedPrice.sub(subjectPrice).toJSON(), month };
      });

    return makeCustomMonthlyPriceInput({
      effectiveDate,
      compSequence,
      transactionEntities,
      trends,
      subjectMarketPrice: subjectPrice.toJSON(),
      trendsAsPercentages: false,
    });
  },
);

export const calculateDateOfSaleOrListToSaleAdjustment = ({
  price,
  marketAdjustments,
  listingStatus,
  contractDate,
  saleDate,
  effectiveDate,
  zillowMethodInput,
  customMonthlyPriceMethodInput,
}: {
  price?: AloftPriceJSON;
  marketAdjustments: MarketAdjustments;
  listingStatus: Transaction['status'];
  contractDate?: IsoMonth;
  saleDate?: IsoMonth;
  effectiveDate?: AloftDateJSON;
  zillowMethodInput?: DateOfSaleMethodInput.Zillow;
  customMonthlyPriceMethodInput?: DateOfSaleMethodInput.CustomMonthlyPrice;
}): AloftPrice => {
  if (isUndefined(price) || isUndefined(listingStatus)) return AloftPrice.zero;

  const shouldApplyListToSale = getShouldApplyListToSale(listingStatus);
  if (shouldApplyListToSale) {
    return calculateListToSaleAdjustment({
      price: schAloftPrice.parse(price),
      listToSale: marketAdjustments.cachedForms.listToSale,
    });
  }

  const shouldApplyDateOfSale = getShouldApplyDateOfSale(listingStatus);
  if (shouldApplyDateOfSale) {
    return calculateDateOfSaleAdjustment({
      price: schAloftPrice.parse(price),
      dateOfSale: marketAdjustments.cachedForms.dateOfSale,
      saleDate,
      contractDate,
      effectiveDate: effectiveDate ? schAloftDate.parse(effectiveDate) : undefined,
      zillowMethodInput,
      customMonthlyPriceMethodInput,
    });
  }

  return AloftPrice.zero;
};

export const selectDateOfSaleOrListToSaleAdjustment = createSelector(
  [
    selectTransactionById,
    selectMarketAdjustments,
    selectContractDate,
    selectSaleDate,
    selectEffectiveDate,
    selectZillowMethodInput,
    selectCustomMonthlyPriceMethodInput,
  ],
  (
    transaction,
    marketAdjustments,
    contractDate,
    saleDate,
    effectiveDate,
    zillowMethodInput,
    customMonthlyPriceMethodInput,
  ) =>
    calculateDateOfSaleOrListToSaleAdjustment({
      price: transaction?.price,
      marketAdjustments,
      listingStatus: transaction?.status,
      contractDate,
      saleDate,
      effectiveDate,
      zillowMethodInput,
      customMonthlyPriceMethodInput,
    }),
);

export const selectAppliedDateOfSaleOrListToSaleAdjustment = createSelector(
  [selectDateOfSaleOrListToSaleAdjustment, selectAdjustmentSettings],
  (dateOfSaleOrListToSaleAdjustment, adjustmentSettings) =>
    applyRoundingAndThresholds(
      dateOfSaleOrListToSaleAdjustment,
      'dateOfSale',
      adjustmentSettings,
    ) ?? AloftPrice.zero,
);

export const selectDateOfSaleOrListToSaleAdjustments = createSelector(
  [
    selectTransactionEntities,
    selectCompSequence,
    selectMarketAdjustments,
    selectEffectiveDate,
    selectZillowMethodInput,
    selectCustomMonthlyPriceMethodInput,
  ],
  (
    transactions,
    compSequence,
    marketAdjustments,
    effectiveDate,
    zillowMethodInput,
    customMonthlyPriceMethodInput,
  ) =>
    Object.fromEntries(
      compSequence.map((id) => {
        const transaction = transactions[id];

        return [
          id,
          calculateDateOfSaleOrListToSaleAdjustment({
            listingStatus: transaction?.status,
            price: transaction?.price,
            contractDate: transaction?.contractDate,
            saleDate: transaction?.saleDate,
            marketAdjustments,
            effectiveDate,
            zillowMethodInput,
            customMonthlyPriceMethodInput,
          }),
        ];
      }),
    ),
);

export const selectAppliedDateOfSaleOrListToSaleAdjustments = createSelector(
  [selectDateOfSaleOrListToSaleAdjustments, selectAdjustmentSettings],
  (dateOfSaleOrListToSaleAdjustments, adjustmentSettings) =>
    Object.fromEntries(
      Object.entries(dateOfSaleOrListToSaleAdjustments).map(([id, adj]) => [
        id,
        applyRoundingAndThresholds(adj, 'dateOfSale', adjustmentSettings) ?? AloftPrice.zero,
      ]),
    ),
);
